import React from 'react'
import { useTranslation } from 'react-i18next'
import { Table, TableBody, TableRow, TableHead } from '@material-ui/core'
import { sum } from 'lodash'
import {
  energyPerLengthUnit,
  formatEnergyPerLength,
  formatPowerPerLength,
  powerPerLengthUnit,
} from '../../../util/formatters'
import { TableHeaderTitleCell } from '../report/TableHeaderTitleCell'
import { TableHeaderUnitCell } from '../report/TableHeaderUnitCell'
import { TableValueCell } from '../../common/TableValueCell'
import { TableTitleCell } from '../../common/TableTitleCell'

type BoreholeFieldContainer = {
  holeCount?: number | null
  holeDepth?: number | null
}

type Props = {
  container: BoreholeFieldContainer
  harvested: number[]
  wasteInput: number[]
}

export function BoreholeFieldInherentNumbers({
  container,
  harvested,
  wasteInput,
}: Props): React.ReactElement {
  const { t } = useTranslation()

  const totalDepth = (container.holeCount ?? 0) * (container.holeDepth ?? 0)

  const totalEnergyHarvest = sum(harvested)
  const maxPowerHarvest = Math.max(...harvested, 0)
  const totalEnergyInject = sum(wasteInput)
  const maxPowerInject = Math.max(...wasteInput, 0)

  const inherentHarvestedEnergy = formatEnergyPerLength(
    totalEnergyHarvest,
    totalDepth,
    {
      omitUnit: true,
    },
  )
  const inherentHarvestedPower = formatPowerPerLength(
    maxPowerHarvest,
    totalDepth,
    { omitUnit: true },
  )
  const inherentWasteInputEnergy = formatEnergyPerLength(
    totalEnergyInject,
    totalDepth,
    { omitUnit: true },
  )
  const inherentWasteInputPower = formatPowerPerLength(
    maxPowerInject,
    totalDepth,
    { omitUnit: true },
  )

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeaderTitleCell title={t('boreholeField.inherentNumbers')} />
          <TableHeaderUnitCell
            firstRow={t('common.total')}
            secondRow={energyPerLengthUnit}
          />
          <TableHeaderUnitCell
            firstRow={t('common.max')}
            secondRow={powerPerLengthUnit}
          />
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>
          <TableTitleCell title={t('boreholeField.inherentHarvested')} />
          <TableValueCell value={inherentHarvestedEnergy} />
          <TableValueCell value={inherentHarvestedPower} />
        </TableRow>
        <TableRow>
          <TableTitleCell title={t('boreholeField.inherentWasteInput')} />
          <TableValueCell value={inherentWasteInputEnergy} />
          <TableValueCell value={inherentWasteInputPower} />
        </TableRow>
      </TableBody>
    </Table>
  )
}
